import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../api/axiosConfig';
import { apiErrorText } from '../utils/apiErrors';
import { isoWeek, isoWeekYear, parseISODate, todayISO } from '../utils/dates';
import { APPLICATION_TYPES, DOSE_FORMS, DOSE_UNITS } from '../utils/fumigation';
import './CompaniesPage.css';
import './SalesFormPage.css';
import '../styles/topbarButtons.css';
import '../styles/rowActions.css';
import '../styles/fumigation.css';
import '../styles/pageHeader.css';

// Fumigación Aérea (Producción), the form of one aerial fumigation, like a row of the farms'
// "ANEXO D" sheet: farm, date, products and doses, kind of application, volume, fumigation company
// and pilot. Ciclo is assigned by the API (the farm's next of the year), Semana follows the date and
// Frecuencia is the days since the farm's previous fumigation of the year. /fumigacion/:id edits a
// saved one (opened from the Historial de Fumigación).
// Data: fumigations/ (core/views.py FumigationApplicationViewSet) and the Admin catalogs
// fumigation-products/, fumigation-companies/, fumigation-pilots/.

const FARM_KEY = 'farmershub_fumigation_farm';
const COMPANY_KEY = 'farmershub_fumigation_company';
const emptyLine = () => ({ product: '', dose: '', form: '', unit: '' });
const blankForm = () => ({
  date: todayISO(), application_type: 'emulsion', volume: '', fumigated_hectares: '',
  fumigation_company: '', pilot: '', observations: '', products: [emptyLine()],
});

export default function FumigationPage() {
  const { id } = useParams();
  const editingId = id ? Number(id) : null;
  const navigate = useNavigate();

  const [allFarms, setAllFarms] = useState([]);
  const [companyId, setCompanyId] = useState(() => {
    try { return localStorage.getItem(COMPANY_KEY) || ''; } catch { return ''; }
  }); // '' = Todas las empresas
  const [farmId, setFarmId] = useState(() => {
    try { return localStorage.getItem(FARM_KEY) || ''; } catch { return ''; }
  });
  const [catalogs, setCatalogs] = useState({ products: [], companies: [], pilots: [] });
  const [form, setForm] = useState(blankForm);
  const [original, setOriginal] = useState(null); // the fumigation being edited, as saved
  const [yearRows, setYearRows] = useState([]); // the farm's fumigations of the form's year
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('farms/'), api.get('fumigation-products/'), api.get('fumigation-companies/'), api.get('fumigation-pilots/'),
    ]).then(([f, products, companies, pilots]) => {
      setAllFarms(f.data);
      setCatalogs({ products: products.data, companies: companies.data, pilots: pilots.data });
    }).catch(() => setStatus({ type: 'error', text: 'No se pudieron cargar las fincas o los catálogos de fumigación.' }));
  }, []);

  // Editing: load the fumigation and put its farm in the filters.
  useEffect(() => {
    if (!editingId) {
      setOriginal(null);
      setForm(blankForm());
      return;
    }
    api.get(`fumigations/${editingId}/`).then((r) => {
      const f = r.data;
      setOriginal(f);
      setFarmId(String(f.farm));
      setForm({
        date: f.date, application_type: f.application_type, volume: String(Number(f.volume)),
        fumigated_hectares: f.fumigated_hectares == null ? '' : String(Number(f.fumigated_hectares)),
        fumigation_company: String(f.fumigation_company), pilot: String(f.pilot), observations: f.observations || '',
        products: f.products.map((p) => ({
          product: String(p.product), dose: String(Number(p.dose)), form: p.form || '', unit: p.unit || '',
        })),
      });
    }).catch((err) => setStatus({ type: 'error', text: apiErrorText(err, 'No se pudo cargar la fumigación.') }));
  }, [editingId]);

  // Companies come from the farms the user can see; the Finca list follows the chosen company.
  const companies = useMemo(() => {
    const byId = new Map(allFarms.map((f) => [String(f.company), f.company_name]));
    return [...byId].map(([cid, name]) => ({ id: cid, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [allFarms]);
  const farms = useMemo(
    () => (companyId ? allFarms.filter((f) => String(f.company) === companyId) : allFarms),
    [allFarms, companyId],
  );

  // Keep the company and farm valid for what the user can see (an edited fumigation's farm wins).
  useEffect(() => {
    if (!allFarms.length) return;
    if (companyId && !companies.some((c) => c.id === companyId)) {
      setCompanyId('');
      return;
    }
    try { localStorage.setItem(COMPANY_KEY, companyId); } catch { /* ignore */ }
    if (farms.some((f) => String(f.id) === farmId)) return;
    if (farmId && allFarms.some((f) => String(f.id) === farmId)) {
      setCompanyId(''); // the farm belongs to another company: show every company
      return;
    }
    setFarmId(String(farms[0]?.id || ''));
  }, [allFarms, companies, companyId, farms, farmId]);

  // A new fumigation starts from the farm's Hectáreas fumigadas (they can be changed in the form).
  const farmFumigatedHa = allFarms.find((f) => String(f.id) === farmId)?.fumigated_hectares;
  useEffect(() => {
    if (editingId) return;
    setForm((f) => ({ ...f, fumigated_hectares: farmFumigatedHa == null ? '' : String(Number(farmFumigatedHa)) }));
  }, [farmId, farmFumigatedHa, editingId]);

  // The farm's fumigations of the form's year: the next Ciclo and the Frecuencia come from them.
  const formYear = form.date ? isoWeekYear(parseISODate(form.date)) : null;
  useEffect(() => {
    if (!farmId || !formYear) {
      setYearRows([]);
      return undefined;
    }
    if (!editingId) {
      try { localStorage.setItem(FARM_KEY, farmId); } catch { /* ignore */ }
    }
    let stale = false;
    api.get('fumigations/', { params: { farm: farmId, year: formYear } })
      .then((r) => { if (!stale) setYearRows(r.data); })
      .catch(() => { if (!stale) setYearRows([]); });
    return () => { stale = true; };
  }, [farmId, formYear, editingId]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const setFumigationCompany = (e) => {
    const value = e.target.value;
    // A pilot of another company no longer fits.
    setForm((f) => ({
      ...f,
      fumigation_company: value,
      pilot: catalogs.pilots.some((p) => String(p.id) === f.pilot && String(p.fumigation_company) === value) ? f.pilot : '',
    }));
  };
  const setLine = (i, key, value) => setForm((f) => ({
    ...f,
    products: f.products.map((l, j) => {
      if (j !== i) return l;
      const line = { ...l, [key]: value };
      // Choosing a product brings its usual Tipo and Unidad (Admin · Productos de Fumigación);
      // they can still be changed on the line.
      if (key === 'product') {
        const product = catalogs.products.find((p) => String(p.id) === value);
        if (product?.form) {
          line.form = product.form;
          line.unit = product.unit || '';
        }
      }
      // A unit of the other form (e.g. litros on a polvo) no longer fits.
      if (key === 'form' && !(DOSE_UNITS[value] || []).some(([u]) => u === line.unit)) line.unit = '';
      return line;
    }),
  }));
  const addLine = () => setForm((f) => ({ ...f, products: [...f.products, emptyLine()] }));
  const removeLine = (i) => setForm((f) => ({
    ...f, products: f.products.length > 1 ? f.products.filter((_, j) => j !== i) : [emptyLine()],
  }));

  const pilots = useMemo(
    () => catalogs.pilots.filter((p) => String(p.fumigation_company) === form.fumigation_company),
    [catalogs.pilots, form.fumigation_company],
  );
  const week = form.date ? isoWeek(parseISODate(form.date)) : null;
  const others = useMemo(() => yearRows.filter((r) => r.id !== editingId), [yearRows, editingId]);
  // An edited fumigation keeps its cycle unless it moves to another farm or year.
  const keepsCycle = original && String(original.farm) === farmId && original.year === formYear;
  const cycle = keepsCycle ? original.cycle : others.reduce((m, r) => Math.max(m, r.cycle), 0) + 1;
  // Days since the latest earlier fumigation of this farm in the year (as the API does for the historial).
  const frequency = useMemo(() => {
    if (!form.date) return null;
    const earlier = others.filter((r) => r.date < form.date).map((r) => r.date).sort();
    if (!earlier.length) return null;
    return Math.round((parseISODate(form.date) - parseISODate(earlier[earlier.length - 1])) / 86400000);
  }, [others, form.date]);

  const filledLines = form.products.filter((l) => l.product || l.dose || l.form || l.unit);
  const usedProducts = form.products.map((l) => l.product).filter(Boolean);
  const isComplete = farmId && form.date && form.application_type && form.volume !== ''
    && form.fumigation_company && form.pilot
    && filledLines.length > 0 && filledLines.every((l) => l.product && l.dose !== '' && l.form && l.unit);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isComplete) return;
    setSaving(true);
    setStatus(null);
    const body = {
      farm: Number(farmId), date: form.date, application_type: form.application_type,
      volume: form.volume, fumigated_hectares: form.fumigated_hectares === '' ? null : form.fumigated_hectares,
      fumigation_company: Number(form.fumigation_company), pilot: Number(form.pilot),
      observations: form.observations.trim(),
      products: filledLines.map((l) => ({ product: Number(l.product), dose: l.dose, form: l.form, unit: l.unit })),
    };
    try {
      if (editingId) {
        await api.put(`fumigations/${editingId}/`, body);
        navigate('/fumigacion/historial');
        return;
      }
      const saved = await api.post('fumigations/', body);
      setStatus({ type: 'success', text: `Ciclo ${saved.data.cycle} de ${saved.data.farm_name} guardado.` });
      setForm({ ...blankForm(), fumigated_hectares: farmFumigatedHa == null ? '' : String(Number(farmFumigatedHa)) });
      setYearRows((rows) => [...rows, saved.data]);
    } catch (err) {
      setStatus({ type: 'error', text: apiErrorText(err, 'No se pudo guardar la fumigación.') });
    } finally {
      setSaving(false);
    }
  };

  const farmName = allFarms.find((f) => String(f.id) === farmId)?.name || '';
  const missingCatalogs = !catalogs.products.length || !catalogs.companies.length || !catalogs.pilots.length;

  return (
    <div className="companies-root">
      <div className="page-topbar">
        <Link to="/production" className="topbar-btn">← Producción</Link>
        <Link to="/fumigacion/historial" className="topbar-btn">📋 Historial de Fumigación</Link>
      </div>
      <h1 className="companies-title page-header-divided">✈️ {editingId ? 'Editar Fumigación Aérea' : 'Fumigación Aérea'}</h1>

      <div className="history-filters">
        {companies.length > 1 && (
          <select className="history-select" value={companyId} onChange={(e) => setCompanyId(e.target.value)} aria-label="Empresa">
            <option value="">Todas las empresas</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
        <select className="history-select" value={farmId} onChange={(e) => setFarmId(e.target.value)} aria-label="Finca">
          {farms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
      </div>

      {missingCatalogs && (
        <div className="companies-status companies-status-error">
          Faltan productos, compañías o pilotos de fumigación: agréguelos en Admin antes de registrar.
        </div>
      )}
      {status && <div className={`companies-status companies-status-${status.type}`}>{status.text}</div>}

      <form className="companies-card fum-form" onSubmit={handleSubmit}>
        <div className="fum-form-title">
          {editingId ? `Editar ciclo ${original?.cycle ?? ''}` : 'Nueva fumigación'} · {farmName}
        </div>

        <div className="fum-grid">
          <div className="fum-field">
            <span>Ciclo</span>
            <div className="fum-auto" title="Se asigna solo: el siguiente ciclo de la finca en el año">{cycle}</div>
          </div>
          <label className="fum-field">
            <span>Fecha</span>
            <input type="date" className="fum-input" value={form.date} onChange={set('date')} required />
          </label>
          <div className="fum-field">
            <span>Semana</span>
            <div className="fum-auto">{week ?? '—'}</div>
          </div>
          <div className="fum-field">
            <span>Frecuencia</span>
            <div className="fum-auto" title="Días desde la fumigación anterior del año">
              {frequency == null ? '—' : `${frequency} días`}
            </div>
          </div>
          <label className="fum-field">
            <span>Tipo de aplicación</span>
            <select className="fum-input" value={form.application_type} onChange={set('application_type')}>
              {APPLICATION_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <label className="fum-field">
            <span>Volumen</span>
            <input
              type="number" min="0" step="0.01" className="fum-input" value={form.volume}
              onChange={set('volume')} placeholder="5,45" required
            />
          </label>
          <label className="fum-field">
            <span>Hectáreas fumigadas</span>
            <input
              type="number" min="0" step="0.01" className="fum-input" value={form.fumigated_hectares}
              onChange={set('fumigated_hectares')} placeholder="Hectáreas"
              title="Se llena con las hectáreas fumigadas de la finca; puede cambiarlas para esta fumigación"
            />
            {farmFumigatedHa != null && form.fumigated_hectares !== '' && Number(form.fumigated_hectares) !== Number(farmFumigatedHa) && (
              <span className="fum-field-hint">La finca tiene {Number(farmFumigatedHa).toLocaleString('es-EC')} ha</span>
            )}
          </label>
          <label className="fum-field">
            <span>Compañía</span>
            <select className="fum-input" value={form.fumigation_company} onChange={setFumigationCompany} required>
              <option value="">Seleccione…</option>
              {catalogs.companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <label className="fum-field">
            <span>Piloto</span>
            <select
              className="fum-input" value={form.pilot} onChange={set('pilot')} required disabled={!form.fumigation_company}
            >
              <option value="">{form.fumigation_company ? 'Seleccione…' : 'Elija la compañía primero'}</option>
              {pilots.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
        </div>

        <div className="fum-products">
          <div className="fum-products-head">Productos y dosis</div>
          {form.products.map((line, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={i} className="fum-product-line">
              <select
                className="fum-input fum-product-select" value={line.product} aria-label={`Producto ${i + 1}`}
                onChange={(e) => setLine(i, 'product', e.target.value)}
              >
                <option value="">Producto…</option>
                {catalogs.products.map((p) => (
                  <option key={p.id} value={p.id} disabled={p.id !== Number(line.product) && usedProducts.includes(String(p.id))}>
                    {p.name}
                  </option>
                ))}
              </select>
              <input
                type="number" min="0" step="any" className="fum-input fum-dose" value={line.dose} placeholder="Dosis"
                aria-label={`Dosis ${i + 1}`} onChange={(e) => setLine(i, 'dose', e.target.value)}
              />
              <select
                className="fum-input fum-form-select" value={line.form} aria-label={`Tipo ${i + 1}`}
                onChange={(e) => setLine(i, 'form', e.target.value)}
              >
                <option value="">Tipo…</option>
                {DOSE_FORMS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
              <select
                className="fum-input fum-unit-select" value={line.unit} aria-label={`Unidad ${i + 1}`}
                onChange={(e) => setLine(i, 'unit', e.target.value)} disabled={!line.form}
              >
                <option value="">{line.form ? 'Unidad…' : 'Elija el tipo'}</option>
                {(DOSE_UNITS[line.form] || []).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
              <button type="button" className="row-icon-btn" onClick={() => removeLine(i)} title="Quitar" aria-label="Quitar producto">✖️</button>
            </div>
          ))}
          <button type="button" className="fum-add-line" onClick={addLine}>+ Agregar producto</button>
        </div>

        <label className="fum-field fum-observations">
          <span>Observaciones</span>
          <textarea className="fum-input" rows={2} value={form.observations} onChange={set('observations')} />
        </label>

        <div className="fum-actions">
          {editingId && <Link to="/fumigacion/historial" className="topbar-btn">Cancelar</Link>}
          <button type="submit" className="topbar-btn topbar-btn-primary" disabled={saving || !isComplete}>
            {saving ? 'Guardando…' : editingId ? '💾 Guardar cambios' : '💾 Guardar fumigación'}
          </button>
        </div>
      </form>
    </div>
  );
}
